/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable no-param-reassign */
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Grid, IconButton } from "@mui/material";
import * as Cesium from "cesium";
import React from "react";
import { useTranslation } from "react-i18next";
import { useMap3DObjectApi } from "~/api/GeneratedApi";
import DraggableDialog from "~/components/DraggableDialog";
import { setTerrainAdjustmentDialogOpen } from "~/redux/reducers/mapReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { useAccess } from "~/utils/hooks/useAccess";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { SavedElevationSettings } from "../../functions/colorAdjustments";
import { CustomTerrainProvider } from "../../types/CesiumMapItems";
import { TerrainColorAdjustment } from "./TerrainColorAdjustment";

interface Props {
  viewer: Cesium.Viewer;
}

export const ShadingModes = ["elevation", "no-shading"] as const;
export type ShadingMode = typeof ShadingModes[number];

export const TerrainAdjustmentDialog = ({
  viewer,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("cesium");
  const dispatch = useAppDispatch();
  const { Map3DObjectUpdate } = useAccess();
  const open = useAppSelector((s) => s.map.terrainAdjustmentDialogOpen);
  const [shadingMode, setShadingMode] =
    React.useState<ShadingMode>("no-shading");
  const { updateCesiumSettings } = useMap3DObjectApi();
  const handleClose = React.useCallback(() => {
    dispatch(setTerrainAdjustmentDialogOpen(false));
  }, [dispatch]);
  const [settingsToSave, setSettingsToSave] = React.useState<string>();

  const { mutateAsync: saveAsync } = useCustomMutation(
    ({ settings, id }: { settings: string; id: number }) =>
      updateCesiumSettings({ elevationSettings: settings }, id),
  );

  const handleSettingsChange = React.useCallback((val: string) => {
    setSettingsToSave(val);
  }, []);

  const handleSave = React.useCallback(async () => {
    if (!settingsToSave) return;
    await saveAsync({
      settings: settingsToSave,
      id: (viewer.terrainProvider as CustomTerrainProvider).customId,
    });
    const parsed = settingsToSave
      ? (JSON.parse(settingsToSave) as SavedElevationSettings)
      : undefined;
    parsed?.colorsAndSteps?.forEach((s) => {
      if (s.isLowest) s.stepMin = Number.NEGATIVE_INFINITY;
      if (s.isHighest) s.stepMax = Number.POSITIVE_INFINITY;
    });
    (viewer.terrainProvider as CustomTerrainProvider).elevationSettings =
      parsed;
  }, [saveAsync, settingsToSave, viewer.terrainProvider]);

  if (!open) return null;

  return (
    <DraggableDialog
      open={open}
      title={t("adjust-terrain")}
      toolbar={
        <IconButton
          style={{ marginLeft: "auto" }}
          onClick={handleClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      }
      actions={
        Map3DObjectUpdate ? (
          <Button
            color="secondary"
            variant="contained"
            onClick={handleSave}
            disabled={!settingsToSave}
          >
            {t("general:save")}
          </Button>
        ) : undefined
      }
      transitionDuration={{ exit: 0 }}
      maxWidth="xs"
    >
      <Box p={2}>
        <Grid container spacing={2}>
          <TerrainColorAdjustment
            viewer={viewer}
            onSettingsChange={handleSettingsChange}
            shadingMode={shadingMode}
            onShadingModeChange={setShadingMode}
          />
        </Grid>
      </Box>
    </DraggableDialog>
  );
};
